// ===================================
// LANDING PAGE - VERIFIED COMPLETE
// src/pages/LandingPage.js
// Stage 11 Fix: All sections verified
// ===================================

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/layout/PublicNavbar';
import './LandingPage.css';

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
            // Also add visible class directly for CSS animations
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    // Small delay to ensure DOM is ready
    setTimeout(() => {
      document.querySelectorAll('.animate-on-scroll').forEach((el) => {
        observer.observe(el);
      });
    }, 100);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page">
      <PublicNavbar />

      {/* Animated Background */}
      <div className="gradient-bg">
        <div className="gradient-orb gradient-orb-1"></div>
        <div className="gradient-orb gradient-orb-2"></div>
        <div className="gradient-orb gradient-orb-3"></div>
      </div>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">AI-Powered Interview Coaching</div>
            <h1>
              Master Your Interview,<br />
              <span className="highlight">Land Your Dream Job</span>
            </h1>
            <p className="hero-description">
              Practice with our AI interviewer that adapts to your experience level. Get instant feedback, detailed transcripts, and actionable tips to ace your next HR interview.
            </p>
            <div className="hero-cta-group">
              <Link to="/register" className="btn btn-primary">
                Start Practicing Now
                <span>→</span>
              </Link>
              <a href="#how-it-works" className="btn btn-secondary">
                See How It Works
              </a>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <div className="stat-value">1K+</div>
                <div className="stat-label">Interviews Conducted</div>
              </div>
              <div className="stat">
                <div className="stat-value">94%</div>
                <div className="stat-label">Success Rate</div>
              </div>
              <div className="stat">
                <div className="stat-value">4.9★</div>
                <div className="stat-label">User Rating</div>
              </div>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-mockup">
              <div className="mockup-dots">
                <div className="mockup-dot"></div>
                <div className="mockup-dot"></div>
                <div className="mockup-dot"></div>
              </div>
              <div className="mockup-content">
                <div className="chat-message">
                  <div className="chat-avatar ai">🎯</div>
                  <div className="chat-bubble ai">Tell me about a time you faced a challenging situation at work.</div>
                </div>
                <div className="chat-message user">
                  <div className="chat-avatar user">👤</div>
                  <div className="chat-bubble user">In my previous role, we had a critical deadline...</div>
                </div>
                <div className="chat-message">
                  <div className="chat-avatar ai">🎯</div>
                  <div className="chat-bubble ai">Great start! Try using the STAR method to structure your response more effectively.</div>
                </div>
                <div className="chat-message user">
                  <div className="chat-avatar user">👤</div>
                  <div className="chat-bubble user">Thanks! Let me try again with that framework...</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =============================================
          FEATURES SECTION
          ============================================= */}
      <section className="features" id="features">
        <div className="section-header animate-on-scroll">
          <div className="section-label">Features</div>
          <h2 className="section-title">Everything You Need to Succeed</h2>
          <p className="section-description">
            Our AI coach combines cutting-edge technology with proven interview techniques to give you an unfair advantage.
          </p>
        </div>

        <div className="features-grid">
          {[
            { icon: '🎭', title: 'Adaptive Interviewer Styles', desc: 'Choose from friendly, strict, or technical interviewer personalities to prepare for any interview scenario.' },
            { icon: '📊', title: 'Detailed Performance Scoring', desc: 'Get instant AI-generated scores on clarity, relevance, confidence, and areas for improvement after each session.' },
            { icon: '📝', title: 'Full Transcripts', desc: 'Review complete transcripts of your sessions to identify patterns and track your progress over time.' },
            { icon: '🎯', title: 'Experience-Level Matching', desc: 'Questions tailored to entry, intermediate, or senior roles ensure relevant and challenging practice.' },
            { icon: '⚡', title: 'Instant Availability', desc: 'Practice anytime, anywhere. No scheduling, no waiting. Your AI coach is available 24/7.' },
            { icon: '📈', title: 'Progress Dashboard', desc: 'Track your improvement with detailed analytics, score trends, and personalized recommendations.' },
          ].map((feature, index) => (
            <div key={index} className="feature-card animate-on-scroll">
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* =============================================
          HOW IT WORKS SECTION
          ============================================= */}
      <section className="how-it-works" id="how-it-works">
        <div className="section-header animate-on-scroll">
          <div className="section-label">How It Works</div>
          <h2 className="section-title">Start Practicing in Minutes</h2>
          <p className="section-description">
            Our simple process gets you from sign-up to your first practice session in no time.
          </p>
        </div>

        <div className="steps-container">
          {[
            { num: 1, title: 'Create Account', desc: 'Quick sign-up with email. No credit card required for trial.' },
            { num: 2, title: 'Choose Your Session', desc: 'Select duration, interviewer style, and experience level.' },
            { num: 3, title: 'Practice Interview', desc: 'Chat or call with your AI coach for realistic practice.' },
            { num: 4, title: 'Get Feedback', desc: 'Review scores, transcripts, and actionable tips instantly.' },
          ].map((step, index) => (
            <div key={index} className="step animate-on-scroll">
              <div className="step-number">{step.num}</div>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* =============================================
          PRICING SECTION
          ============================================= */}
      <section className="pricing" id="pricing">
        <div className="section-header animate-on-scroll">
          <div className="section-label">Pricing</div>
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-description">
            Choose the session length that works for you. No subscriptions, no hidden fees.
          </p>
        </div>

        <div className="pricing-grid">
          <div className="pricing-card animate-on-scroll">
            <div className="pricing-duration">Quick Practice</div>
            <div className="pricing-price">$6.99<span>/session</span></div>
            <div className="pricing-description">15-minute focused session</div>
            <ul className="pricing-features">
              <li>15 minutes of AI coaching</li>
              <li>Full transcript included</li>
              <li>Performance scoring</li>
              <li>Basic feedback report</li>
            </ul>
            <Link to="/register" className="btn btn-secondary btn-full">Get Started</Link>
          </div>

          <div className="pricing-card popular animate-on-scroll">
            <div className="pricing-duration">Standard Session</div>
            <div className="pricing-price">$11.99<span>/session</span></div>
            <div className="pricing-description">30-minute complete session</div>
            <ul className="pricing-features">
              <li>30 minutes of AI coaching</li>
              <li>Full transcript included</li>
              <li>Detailed performance scoring</li>
              <li>Comprehensive feedback report</li>
              <li>Improvement suggestions</li>
            </ul>
            <Link to="/register" className="btn btn-primary btn-full">Get Started</Link>
          </div>

          <div className="pricing-card animate-on-scroll">
            <div className="pricing-duration">Deep Dive</div>
            <div className="pricing-price">$19.99<span>/session</span></div>
            <div className="pricing-description">60-minute intensive session</div>
            <ul className="pricing-features">
              <li>60 minutes of AI coaching</li>
              <li>Full transcript included</li>
              <li>Advanced performance analytics</li>
              <li>Detailed feedback report</li>
              <li>Personalized action plan</li>
            </ul>
            <Link to="/register" className="btn btn-secondary btn-full">Get Started</Link>
          </div>
        </div>
      </section>

      {/* =============================================
          TESTIMONIALS SECTION
          ============================================= */}
      <section className="testimonials" id="testimonials">
        <div className="section-header animate-on-scroll">
          <div className="section-label">Testimonials</div>
          <h2 className="section-title">Loved by Job Seekers</h2>
          <p className="section-description">
            Join thousands who've transformed their interview skills with Career-Coach.AI
          </p>
        </div>

        <div className="testimonials-grid">
          {[
            { initials: 'SK', name: 'Tolu K.', role: 'Salesforce Consultant / Blackforce Alumni', text: 'The strict interviewer mode really pushed me out of my comfort zone. I went into my actual interview feeling prepared for anything. Got the job!' },
            { initials: 'MR', name: 'Michael R.', role: 'Product Manager  / Blackforce Alumni', text: 'As someone with interview anxiety, being able to practice without judgment was a game-changer. The feedback helped me identify my weak spots quickly.' },
            { initials: 'JL', name: 'Jennifer L.', role: 'Marketing Director  / Blackforce Alumni', text: "I did 5 practice sessions before my interview. The AI's tips on structuring my answers with STAR method made all the difference. Highly recommend!" },
          ].map((testimonial, index) => (
            <div key={index} className="testimonial-card animate-on-scroll">
              <p className="testimonial-text">{testimonial.text}</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">{testimonial.initials}</div>
                <div className="testimonial-info">
                  <h4>{testimonial.name}</h4>
                  <p>{testimonial.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* =============================================
          CTA SECTION
          ============================================= */}
      <section className="cta">
        <div className="cta-container animate-on-scroll">
          <h2>Ready to Ace Your Next Interview?</h2>
          <p>Join thousands of successful job seekers who transformed their interview skills with Career-Coach.AI</p>
          <div className="cta-buttons">
            <Link to="/register" className="btn btn-primary btn-lg">
              Start Practicing Now
              <span>→</span>
            </Link>
            <Link to="/login" className="btn btn-secondary btn-lg">
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* =============================================
          FOOTER
          ============================================= */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-brand">
            <div className="footer-logo">
              <div className="logo-icon">🎯</div>
              <span>Career-Coach.AI</span>
            </div>
            <p>AI-powered interview coaching to help you land your dream job.</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#how-it-works">How It Works</a>
            </div>
            <div className="footer-column">
              <h4>Company</h4>
              <a href="#">About Us</a>
              <a href="#">Contact</a>
              <a href="#">Careers</a>
            </div>
            <div className="footer-column">
              <h4>Legal</h4>
              <a href="#">Privacy Policy</a>
              <a href="#">Terms of Service</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} Career-Coach.AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;